import type { NodeStruct } from '@/types/node-types';
import type { ExpressionAst, JsonValue } from './functional-control-flow';
import {
  getParamValue,
  validateExpressionAst,
} from './functional-control-flow';
import { tryFormatFormula } from './formula-format';

/**
 * The Calculate node (`calculate_node`, Phase 8a): named outputs, each a
 * formula over the bound input. Mirrors `services/workflow_ir/calculating.py`.
 */

export const CALCULATE_NODE_ID = 'calculate_node';
export const CALCULATE_CAPABILITY = 'workflow.calculate.v1';
export const MAX_CALCULATE_OUTPUTS = 32;
export const OUTPUT_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;
export const RESERVED_OUTPUT_NAMES = new Set([
  'value',
  'context',
  'judge',
  'attempt',
  'iteration',
  'it',
  'index',
]);
export const OUTPUT_TYPES = [
  'any',
  'number',
  'text',
  'boolean',
  'list',
  'object',
] as const;
export type OutputType = (typeof OUTPUT_TYPES)[number];

export interface CalculateOutput {
  name: string;
  /** The human/agent face; the backend derives `expression` from it. */
  formula: string;
  /** The tree, when known (set by the backend's preview or a saved config). */
  expression?: ExpressionAst | null;
  type?: OutputType;
}

export interface CalculateConfigValue {
  outputs: CalculateOutput[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const parseJson = <T>(raw: unknown, fallback: T): T => {
  if (typeof raw === 'string') {
    if (!raw.trim()) return fallback;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  }
  return (raw as T) ?? fallback;
};

/** Read the node's `outputs` param (JSON list, possibly serialised). */
export const getCalculateConfig = (
  struct: NodeStruct,
): CalculateConfigValue => {
  const raw = parseJson<unknown[]>(getParamValue(struct, 'outputs'), []);
  const outputs: CalculateOutput[] = [];
  if (Array.isArray(raw)) {
    for (const entry of raw) {
      if (!isRecord(entry)) continue;
      const name = typeof entry.name === 'string' ? entry.name : '';
      const expression = isRecord(entry.expression)
        ? (entry.expression as ExpressionAst)
        : null;
      const formula =
        typeof entry.formula === 'string'
          ? entry.formula
          : (expression && tryFormatFormula(expression)) || '';
      const type = OUTPUT_TYPES.includes(entry.type as OutputType)
        ? (entry.type as OutputType)
        : 'any';
      outputs.push({ name, formula, expression, type });
    }
  }
  return { outputs };
};

/** What the node stores: formula text (plus the tree when we have it). */
export const serializeCalculateOutputs = (
  outputs: CalculateOutput[],
): Array<Record<string, unknown>> =>
  outputs.map((output) => {
    const entry: Record<string, unknown> = {
      name: output.name,
      formula: output.formula,
    };
    if (output.expression) entry.expression = output.expression;
    if (output.type && output.type !== 'any') entry.type = output.type;
    return entry;
  });

export interface CalculateValidationError {
  index: number | null;
  field: 'name' | 'formula' | 'outputs' | 'expression';
  message: string;
}

/** Editor-side checks that need no backend: names, counts, stored trees. */
export const validateCalculateConfig = (
  config: CalculateConfigValue,
): CalculateValidationError[] => {
  const errors: CalculateValidationError[] = [];
  if (config.outputs.length === 0) {
    errors.push({
      index: null,
      field: 'outputs',
      message: 'Add at least one output: a name and a formula.',
    });
  }
  if (config.outputs.length > MAX_CALCULATE_OUTPUTS) {
    errors.push({
      index: null,
      field: 'outputs',
      message: `A Calculate node can have at most ${MAX_CALCULATE_OUTPUTS} outputs.`,
    });
  }
  const seen = new Set<string>();
  config.outputs.forEach((output, index) => {
    const name = output.name.trim();
    if (!name) {
      errors.push({
        index,
        field: 'name',
        message: 'Give this output a name.',
      });
    } else if (!OUTPUT_NAME_RE.test(name)) {
      errors.push({
        index,
        field: 'name',
        message: 'Names are words like score or next_b (letters, digits, _).',
      });
    } else if (RESERVED_OUTPUT_NAMES.has(name)) {
      errors.push({
        index,
        field: 'name',
        message: `'${name}' is reserved for the inputs; pick another name.`,
      });
    } else if (seen.has(name)) {
      errors.push({
        index,
        field: 'name',
        message: `Two outputs are both named '${name}'; names must be unique.`,
      });
    }
    seen.add(name);
    if (!output.formula.trim()) {
      errors.push({
        index,
        field: 'formula',
        message: 'Write what to compute, for example round(score * 100) / 100.',
      });
    }
    if (output.expression) {
      const problem = validateExpressionAst(
        output.expression,
        `outputs[${index}].expression`,
      )[0];
      if (problem) {
        errors.push({ index, field: 'expression', message: problem.message });
      }
    }
  });
  return errors;
};

/**
 * A sample value shaped like this Calculate's result (`{name: null, ...}`),
 * so a downstream field picker can offer the output names before any run.
 */
export const declaredCalculateSample = (
  struct: NodeStruct,
): Record<string, JsonValue> | undefined => {
  if (struct.node_id !== CALCULATE_NODE_ID) return undefined;
  const config = getCalculateConfig(struct);
  if (config.outputs.length === 0) return undefined;
  return Object.fromEntries(
    config.outputs.filter((o) => o.name).map((o) => [o.name, null]),
  );
};

// --- reading a run result ---------------------------------------------------

export interface CalculateTraceEntry {
  name: string;
  formula: string | null;
  value: JsonValue;
  inputs: Array<{ field: string; value?: JsonValue; missing?: boolean }>;
}

export interface CalculateRunSummary {
  result: Record<string, JsonValue>;
  trace: CalculateTraceEntry[];
  truncated: boolean;
  error: {
    code: string;
    message: string;
    outputName?: string | null;
    hint?: string | null;
  } | null;
}

const unwrapEnvelope = (value: unknown): unknown =>
  isRecord(value) && value._node_output === true ? value.data : value;

/** Read `data` + `metadata.calculate` from a node result; null if not a Calculate result. */
export const readCalculateResult = (
  result: unknown,
): CalculateRunSummary | null => {
  if (!isRecord(result)) return null;
  const metadata = isRecord(result.metadata) ? result.metadata : null;
  const calc =
    metadata && isRecord(metadata.calculate) ? metadata.calculate : null;
  if (!calc) return null;
  const data = unwrapEnvelope(result.output_data ?? result.data);
  const trace = isRecord(calc.trace) ? calc.trace : null;
  const entries = trace && Array.isArray(trace.outputs) ? trace.outputs : [];
  const details = isRecord(calc.error_details) ? calc.error_details : null;
  const errorText = typeof result.error === 'string' ? result.error : null;
  return {
    result: isRecord(data) ? (data as Record<string, JsonValue>) : {},
    trace: entries.filter(isRecord).map((entry) => ({
      name: String(entry.name ?? ''),
      formula: typeof entry.formula === 'string' ? entry.formula : null,
      value: entry.value as JsonValue,
      inputs: Array.isArray(entry.inputs)
        ? entry.inputs.filter(isRecord).map((input) => ({
            field: String(input.field ?? ''),
            value: input.value as JsonValue,
            missing: input.missing === true,
          }))
        : [],
    })),
    truncated: trace?.truncated === true,
    error:
      typeof calc.error_code === 'string' && calc.error_code
        ? {
            code: calc.error_code,
            message: errorText
              ? errorText.replace(/^[a-z_]+: /, '')
              : 'The Calculate node failed.',
            outputName:
              details && typeof details.output_name === 'string'
                ? details.output_name
                : null,
            hint:
              details && typeof details.hint === 'string' ? details.hint : null,
          }
        : null,
  };
};

/** Short text for a chip: `next_b = 12` or `3 outputs`. */
export const describeCalculateResult = (
  summary: CalculateRunSummary,
): string => {
  if (summary.error) {
    return summary.error.outputName
      ? `failed: ${summary.error.outputName}`
      : 'failed';
  }
  const names = Object.keys(summary.result);
  if (names.length === 0) return 'no outputs';
  const first = names[0];
  const value = summary.result[first];
  const shown =
    typeof value === 'string'
      ? JSON.stringify(value)
      : value === null
        ? 'null'
        : typeof value === 'object'
          ? Array.isArray(value)
            ? `[${value.length} items]`
            : '{…}'
          : String(value);
  const clipped = shown.length > 24 ? `${shown.slice(0, 23)}…` : shown;
  return names.length === 1
    ? `${first} = ${clipped}`
    : `${first} = ${clipped} · ${names.length - 1} more`;
};
